import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { todayBusiness, addDays, fmtDateApi } from "../utils/date";

/** Active booking without prepayment from /api/bookings/noprepay ({results}). */
export interface NoPrepay {
  id: string | number;
  quest_id: string;
  date: string;
  time: string;
  custom_time?: string | null;
  short_name?: string | null;
  quest_name?: string | null;
  /** Название спецверсии — именно оно называет игру (22.09, владелец). */
  special_version_title?: string | null;
  branch_name?: string | null;
  client_name?: string | null;
  phone?: string | null;
  players?: number | null;
  source?: string | null;
  comment?: string | null;
  created_by_name?: string | null;
  /** канал связи и привязанный TG (26.08, владелец): переписка прямо из карточки */
  channel?: string | null;
  telegram?: string | null;
  /** 08.09: у «удерживаемых» броней (см. keep) предоплата уже стоит */
  prepaid?: number | boolean | null;
  prepaid_amount?: number | null;
}

interface ListResp { results?: NoPrepay[] }

/* 08.09 (владелец): бронь, которой отметили предоплату прямо из списка, остаётся в нём ещё
   5 минут — и после перезагрузки вкладки тоже. В localStorage только id, время и сумма
   (без ПД); строки для них сервер отдаёт по keep=id,id. */
const KEY = "ub.noprepay.recent";
const TTL_MS = 5 * 60_000;
type Recent = Record<string, { ts: number; amt: number }>;
function readRecent(): Recent {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}") as Recent;
    const now = Date.now();
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v && now - Number(v.ts) < TTL_MS));
  } catch { return {}; }
}
function writeRecent(r: Recent): void { try { localStorage.setItem(KEY, JSON.stringify(r)); } catch { /* приватный режим */ } }

export interface PrepayMark { paid: boolean; amt: number }

/** Upcoming window: today → +60 days (DC chases prepayment for upcoming games). */
export function useNoPrepay() {
  const [items, setItems] = useState<NoPrepay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);
  const [recent, setRecent] = useState<Recent>(readRecent);
  /* локальные отметки в этой сессии: главнее строки с сервера (оптимистичный UI карточки) */
  const [marks, setMarks] = useState<Record<string, PrepayMark>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const from = fmtDateApi(todayBusiness());
    const to = fmtDateApi(addDays(todayBusiness(), 60));
    const keep = Object.keys(readRecent());
    api<ListResp>(`/api/bookings/noprepay?from=${from}&to=${to}${keep.length ? `&keep=${keep.join(",")}` : ""}`)
      .then((r) => {
        if (!alive) return;
        setItems(r.results ?? []);
        setMarks({});
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить брони без предоплаты");
        setLoading(false);
      });
    return () => { alive = false; };
  }, [trigger]);

  /* раз в 20 с пересматриваем срок — просроченные карточки уходят сами */
  useEffect(() => {
    const t = window.setInterval(() => setRecent(readRecent()), 20_000);
    return () => window.clearInterval(t);
  }, []);

  const mark = useCallback((id: string, m: PrepayMark) => {
    setMarks((prev) => ({ ...prev, [id]: m }));
    const r = readRecent();
    if (m.paid) r[id] = { ts: r[id]?.ts ?? Date.now(), amt: m.amt }; else delete r[id];
    writeRecent(r); setRecent(r);
  }, []);

  /** Эффективное состояние карточки: локальная отметка → строка сервера → дефолт 1000. */
  const eff = useCallback((b: NoPrepay): PrepayMark => {
    const id = String(b.id);
    const m = marks[id];
    if (m) return m;
    return { paid: !!b.prepaid, amt: Number(b.prepaid_amount) || recent[id]?.amt || 1000 };
  }, [marks, recent]);

  /* видимы: неоплаченные + оплаченные из этого списка не старше 5 минут */
  const visible = items.filter((b) => !eff(b).paid || !!recent[String(b.id)]);

  return { items: visible, loading, error, reload, eff, mark };
}
